<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import TextInput from '@/js/components/TextInput.vue';
    import TextareaInput from '@/js/components/TextareaInput.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';

    // Shared by every place a category can be created or edited (top-level create panel,
    // inline root edit, inline subcategory edit/create) so the fields only exist once.
    // No parent picker - a category's parent is fixed at creation and shown read-only via
    // parentContextName; it can never be changed afterwards (see BackOfficeCategories.vue).
    defineProps<{
        idPrefix: string;
        parentContextName?: string | null;
        isPending: boolean;
        showDelete: boolean;
        isDeleting: boolean;
        deleteDisabled?: boolean;
    }>();

    const emit = defineEmits<{
        submit: [];
        cancel: [];
        delete: [];
    }>();

    // Matches the DB column's max length (CategoryEntityConfiguration on the backend).
    const NAME_MAX_LENGTH = 200;

    const name = defineModel<string>('name', { required: true });
    const prompt = defineModel<string>('prompt', { required: true });
    const isActive = defineModel<boolean>('isActive', { required: true });

    const canSubmit = computed(
        () =>
            name.value.trim().length > 0 &&
            name.value.length <= NAME_MAX_LENGTH &&
            prompt.value.trim().length > 0
    );

    const onSubmit = () => {
        if (!canSubmit.value) return;
        emit('submit');
    };
</script>
<template>
    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <FormGroup>
            <div class="flex flex-col gap-1">
                <label :for="`${idPrefix}-name`" class="text-overline">{{ __('name') }}</label>
                <TextInput :id="`${idPrefix}-name`" v-model="name" :maxlength="NAME_MAX_LENGTH" />
            </div>

            <p v-if="parentContextName" class="text-body text-gray-medium">
                {{ __('back-office-categories-parent') }}: {{ parentContextName }}
            </p>

            <div class="flex flex-col gap-1">
                <label :for="`${idPrefix}-prompt`" class="text-overline">{{ __('prompt') }}</label>
                <TextareaInput :id="`${idPrefix}-prompt`" v-model="prompt" />
            </div>

            <CheckboxInput :id="`${idPrefix}-is-active`" v-model="isActive">
                {{ __('active') }}
            </CheckboxInput>
        </FormGroup>

        <div class="flex justify-between">
            <EchoButton
                v-if="showDelete"
                type="button"
                variant="secondary"
                :disabled="deleteDisabled"
                :title="deleteDisabled ? __('back-office-categories-delete-blocked-has-subcategories') : undefined"
                :loading="isDeleting"
                @click="emit('delete')"
            >
                {{ __('delete') }}
            </EchoButton>
            <div class="flex gap-3">
                <EchoButton
                    type="submit"
                    variant="primary"
                    :disabled="!canSubmit"
                    :loading="isPending"
                >
                    {{ __('save') }}
                </EchoButton>
                <EchoButton type="button" variant="secondary" @click="emit('cancel')">
                    {{ __('cancel') }}
                </EchoButton>
            </div>
        </div>
    </form>
</template>
